// ----------------------------------------------------------------------------
// Section: Product Slider
// ----------------------------------------------------------------------------
//
// This is a section that displays product slider items and optional button
//
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------
import Swiper, { Navigation, Scrollbar, A11y, Keyboard, Pagination } from 'swiper';
import 'snippets/card/card';
import 'snippets/icon/icon';

// Style includes
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/scrollbar';
import 'swiper/css/a11y';
import 'swiper/css/keyboard';
import 'swiper/css/pagination';
import './_product-slider.scss';

// -------------------------------------
// Variables
// -------------------------------------

// Selectors
const PRODUCT_SLIDER_SELECTOR = '.carousel--product-slider-container';
const CAROUSEL_SELECTOR        = '.carousel--product-slider';
const CAROUSEL_SELECTOR_BLOG   = '.carousel--product-slider-blog';
const CAROUSEL_SELECTOR_RELATED = '.carousel--product-slider-related';
const PREV_BUTTON_SELECTOR     = '.product-slider-button-prev';
const NEXT_BUTTON_SELECTOR     = '.product-slider-button-next';
const SCROLLBAR_SELECTOR       = '.swiper-scrollbar';
const PAGINATION_SELECTOR      = '.swiper-pagination--product-slider';
const PAGINATION_SELECTOR_RELATED = '.swiper-pagination--product-slider-related';

// -------------------------------------
// Helpers
// -------------------------------------

const initProductSlider = (productSlider) => {
    const carousel   = productSlider.querySelector(CAROUSEL_SELECTOR)
    const prevButton = productSlider.querySelector(PREV_BUTTON_SELECTOR);
    const nextButton = productSlider.querySelector(NEXT_BUTTON_SELECTOR);
    const scrollbar  = productSlider.querySelector(SCROLLBAR_SELECTOR);

    const swiper = new Swiper(carousel, {
        modules: [Navigation, Scrollbar, A11y, Keyboard, Pagination],
        slidesPerView: 1.4,
        spaceBetween: 10,
        centerInsufficientSlides: true,
        observer: true,
        observeParents: true,
        a11y: false,
        watchOverflow: true, // Do not turn into sliders if number of slides is less than slidesPerView.
        grabCursor: true,

        breakpoints: {
            680: {
                slidesPerView: 2.5,
                spaceBetween: 40,
            },
            1024: {
                slidesPerView: 3.6,
                spaceBetween: 40,
            },
            1440: {
                slidesPerView: 4.1,
                spaceBetween: 40,
            },
            1920: {
                slidesPerView: 4.7,
                spaceBetween: 40,
            }
        },
        navigation: {
          nextEl: nextButton,
          prevEl: prevButton
        },
        scrollbar: {
            el: scrollbar,
            draggable: true,
        }
    });

    swiper.slides.forEach(function(slide, index) {
        const slideLink = slide.querySelector('a');

        slideLink.addEventListener('focus', function(e) {
            swiper.slideTo(index);
        })
    });
}

// The new product carousel component
const initProductCarousel = (productSlider) => {
    const carousel   = productSlider.querySelector(CAROUSEL_SELECTOR)
    const prevButton = productSlider.querySelector(PREV_BUTTON_SELECTOR);
    const nextButton = productSlider.querySelector(NEXT_BUTTON_SELECTOR);
    const pagination  = productSlider.querySelector(PAGINATION_SELECTOR);

    const swiper = new Swiper(carousel, {
        modules: [Navigation, A11y, Keyboard, Pagination],
        slidesPerView: 1.4,
        spaceBetween: 10,
        centerInsufficientSlides: true,
        observer: true,
        observeParents: true,
        a11y: false,
        watchOverflow: true, // Do not turn into sliders if number of slides is less than slidesPerView.
        grabCursor: true,

        breakpoints: {
            680: {
                slidesPerView: 3,
                spaceBetween: 25,
            },
            1024: {
                slidesPerView: 4,
                spaceBetween: 35,
            }
        },
        navigation: {
            nextEl: nextButton,
            prevEl: prevButton
        },
        pagination: {
            el: pagination,
            type: 'bullets',
            clickable: true,
        }
    });

    swiper.slides.forEach(function(slide, index) {
        const slideLink = slide.querySelector('a');

        slideLink.addEventListener('focus', function(e) {
            swiper.slideTo(index);
        })
    });
}

const initProductCarouselBlog = (productSlider) => {
    const carousel   = productSlider.querySelector(CAROUSEL_SELECTOR_BLOG)
    const prevButton = productSlider.querySelector(PREV_BUTTON_SELECTOR);
    const nextButton = productSlider.querySelector(NEXT_BUTTON_SELECTOR);
    const pagination  = productSlider.querySelector(PAGINATION_SELECTOR);

    const swiper = new Swiper(carousel, {
        modules: [Navigation, A11y, Keyboard, Pagination],
        slidesPerView: 1.4,
        spaceBetween: 10,
        centerInsufficientSlides: true,
        observer: true,
        observeParents: true,
        a11y: false,
        watchOverflow: true, // Do not turn into sliders if number of slides is less than slidesPerView.
        grabCursor: true,

        breakpoints: {
            680: {
                slidesPerView: 3.4,
                spaceBetween: 25,
            },
            1024: {
                slidesPerView: 5,
                spaceBetween: 14,
            }
        },
        navigation: {
            nextEl: nextButton,
            prevEl: prevButton
        },
        pagination: {
            el: pagination,
            type: 'bullets',
            clickable: true,
        }
    });

    swiper.slides.forEach(function(slide, index) {
        const slideLink = slide.querySelector('a');

        slideLink.addEventListener('focus', function(e) {
            swiper.slideTo(index);
        })
    });
}

const initProductCarouselRelated = (productSlider) => {
    const carousel   = productSlider.querySelector(CAROUSEL_SELECTOR_RELATED)
    const pagination  = productSlider.querySelector(PAGINATION_SELECTOR_RELATED);

    const swiper = new Swiper(carousel, {
        modules: [Navigation, A11y, Keyboard, Pagination],
        slidesPerView: 1.4,
        spaceBetween: 14,
        centerInsufficientSlides: true,
        observer: true,
        observeParents: true,
        a11y: false,
        watchOverflow: true, // Do not turn into sliders if number of slides is less than slidesPerView.
        grabCursor: true,

        breakpoints: {
            1024: {
                slidesPerView: 3,
                spaceBetween: 20
            }
        },
        pagination: {
            el: pagination,
            type: 'bullets',
            clickable: true,
        }
    });

    swiper.slides.forEach(function(slide, index) {
        const slideLink = slide.querySelector('a');

        slideLink.addEventListener('focus', function(e) {
            swiper.slideTo(index);
        })
    });
}

// -------------------------------------
// Hooks
// -------------------------------------

document.addEventListener('DOMContentLoaded', () => {
    const productSliders = document.querySelectorAll(PRODUCT_SLIDER_SELECTOR);

    productSliders.forEach((productSlider) => {
        initProductSlider(productSlider);
        initProductCarousel(productSlider);
        initProductCarouselBlog(productSlider);
        initProductCarouselRelated(productSlider);
    });
});
